import { useQuery } from "@tanstack/vue-query";
import { toValue, watch, type MaybeRefOrGetter } from "vue";
import {
	checkActiveRegion,
	getEnvironmentActiveRegion,
	type ActiveRegion,
	type Environment,
} from "../configuration/aws-configuration";

const FAILOVER_STATUS_POLL_INTERVAL_MS = 30 * 1000;

/**
 * Polls the environment's failover status flag (status.json, served through
 * StatusCloudDistribution) every 30s and calls `onRegionChange` whenever the
 * resolved active region actually flips (primary <-> failover) - never on a
 * poll that just confirms the region we're already on.
 *
 * Mount this once at the app root (App.vue), not per-view - it needs to keep
 * running for the whole session regardless of navigation.
 */
export function useGlobalFailoverHook(
	environment: MaybeRefOrGetter<Environment>,
	onRegionChange: (region: ActiveRegion) => void | Promise<void>,
) {
	// Baseline against whatever boot-time resolution already applied, so the
	// first poll tick doesn't re-fire onRegionChange for a value we already
	// configured everything for at startup.
	let lastAppliedRegion: ActiveRegion | undefined;

	watch(
		() => toValue(environment),
		env => {
			lastAppliedRegion = getEnvironmentActiveRegion(env);
		},
		{ immediate: true },
	);

	const query = useQuery({
		queryKey: ["global-failover-status", environment],
		queryFn: () => checkActiveRegion(toValue(environment)),
		refetchInterval: FAILOVER_STATUS_POLL_INTERVAL_MS,
	});

	watch(query.data, region => {
		if (region === undefined || region === lastAppliedRegion) return;
		lastAppliedRegion = region;
		void onRegionChange(region);
	});

	return query;
}
